import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Zap, Shield, CheckCircle2, XCircle } from 'lucide-react';
export function ProcessSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const phases = [{
    num: '1',
    title: 'Fase de Reset (Días 1-7)',
    desc: 'Limpiamos los receptores hormonales y desplomamos el cortisol acumulado.',
    icon: RefreshCw,
    color: 'text-[#00d4ff]',
    bg: 'bg-[#00d4ff]',
    border: 'border-[#00d4ff]'
  }, {
    num: '2',
    title: 'Fase de Carga (Días 8-14)',
    desc: 'Introducimos los ladrillos biológicos (grasas y minerales) para disparar la producción natural de T.',
    icon: Zap,
    color: 'text-amber-500',
    bg: 'bg-amber-500',
    border: 'border-amber-500'
  }, {
    num: '3',
    title: 'Fase de Blindaje (Días 15-21)',
    desc: 'Optimizamos el flujo sanguíneo y el control mental para un desempeño total.',
    icon: Shield,
    color: 'text-green-500',
    bg: 'bg-green-500',
    border: 'border-green-500'
  }];
  return <section className="py-24 px-4 bg-[#111827] relative border-t border-white/5" data-id="element-147">
      <div className="max-w-5xl mx-auto" data-id="element-148">
        {/* How It Works Timeline */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="mb-32" data-id="element-149">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-center text-white uppercase tracking-tight mb-16" data-id="element-150">
            Cómo funciona el método
          </motion.h2>

          <div className="relative max-w-2xl mx-auto" data-id="element-151">
            {/* Vertical Line */}
            <div className="absolute left-6 md:left-8 top-8 bottom-8 w-0.5 bg-slate-800" data-id="element-152" />

            <div className="space-y-12" data-id="element-153">
              {phases.map((phase, idx) => {
              const Icon = phase.icon;
              return <motion.div key={idx} variants={itemVariants} className="relative flex items-start gap-6 md:gap-8" data-id="element-154">
                    <div className={`relative z-10 w-12 h-12 md:w-16 md:h-16 rounded-full ${phase.bg}/10 border-2 ${phase.border} flex items-center justify-center shrink-0 bg-[#111827]`} data-id="element-155">
                      <Icon className={`w-6 h-6 md:w-8 md:h-8 ${phase.color}`} data-id="element-156" />
                    </div>
                    <div className="pt-2 md:pt-4" data-id="element-157">
                      <h3 className={`text-xl md:text-2xl font-bold uppercase tracking-wide mb-2 ${phase.color}`} data-id="element-158">
                        {phase.title}
                      </h3>
                      <p className="text-slate-300 text-lg leading-relaxed" data-id="element-159">
                        {phase.desc}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </motion.div>

        {/* Who This Is For / Not For */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-160">
          {/* FOR */}
          <motion.div variants={itemVariants} className="bg-[#1a1f2e] rounded-xl p-8 border-t-4 border-green-500 shadow-lg" data-id="element-161">
            <h3 className="text-2xl font-bold text-white uppercase mb-6 flex items-center gap-3" data-id="element-162">
              <CheckCircle2 className="text-green-500 w-8 h-8" data-id="element-163" />
              Para quién es esto
            </h3>
            <ul className="space-y-4" data-id="element-164">
              {["Hombres de 35 a 60 años que se sienten 'apagados' o crónicamente cansados.", 'Profesionales con altos niveles de estrés que han perdido el enfoque y la libido.', 'Padres de familia que quieren recuperar la chispa y la confianza en la intimidad.', 'Hombres que prefieren soluciones biológicas naturales sobre fármacos.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-165">
                  <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-1" data-id="element-166" />
                  <span className="text-slate-300" data-id="element-167">{text}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* NOT FOR */}
          <motion.div variants={itemVariants} className="bg-[#1a1f2e] rounded-xl p-8 border-t-4 border-red-500 shadow-lg" data-id="element-168">
            <h3 className="text-2xl font-bold text-white uppercase mb-6 flex items-center gap-3" data-id="element-169">
              <XCircle className="text-red-500 w-8 h-8" data-id="element-170" />
              Para quién NO es
            </h3>
            <ul className="space-y-4" data-id="element-171">
              {["Quienes buscan una 'cura mágica' de una sola noche sin cambiar ni un hábito.", 'Hombres menores de 25 años que no tienen un desbalance hormonal por estrés.', 'Personas que no están dispuestas a seguir un plan estructurado por 21 días.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-172">
                  <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-1" data-id="element-173" />
                  <span className="text-slate-300" data-id="element-174">{text}</span>
                </li>)}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}